import {
  Box,
  InputAdornment,
  InputLabel,
  Stack,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePickerProps, DesktopDatePicker } from '@mui/x-date-pickers';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
  useFormContext,
} from 'react-hook-form';
import { MAX_DATE, MIN_DATE } from 'src/constants/date';
import { getAvailablePredefinedDates } from 'src/constants/prejudice/defaultValues';
import { EditDatesOptions, PrejudiceType } from 'src/types/prejudice.type';
import { Procedure } from 'src/types/procedure.type';
import { Victime } from 'src/types/victime.type';

const PREDEFINED_DATES: { label: string; value: EditDatesOptions }[] = [
  { label: "Date d'accident", value: 'dateAccident' },
  { label: 'Date de consolidation', value: 'dateConsolidation' },
  { label: 'Date de liquidation', value: 'dateLiquidation' },
  { label: 'Date de décès', value: 'dateDeces' },
];

const MAX_DISPLAYED_PREDEFINED_DATES = 4;

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName> {
  TextFieldProps?: Partial<TextFieldProps>;
  required?: boolean;
  victime: Pick<Victime, 'dateAccident' | 'dateDeces'>;
  procedure: Pick<Procedure, 'dateConsolidation' | 'dateLiquidation'>;
  prejudiceType: PrejudiceType;
  availablePredefinedDates?: EditDatesOptions[];
}

export const PredefinedDatePickerForm = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  TextFieldProps,
  required,
  victime,
  procedure,
  prejudiceType,
  ...props
}: Props<TFieldValues, TName> &
  Omit<
    DatePickerProps<Date>,
    'onChange' | 'value' | 'renderInput' | 'date' | 'openPicker' | 'rawValue'
  >) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedPredefinedDate, setSelectedPredefinedDate] = useState<
    string | null
  >(null);
  const { setValue, getValues } = useFormContext();
  const {
    field: { value, ref: fieldRef, ...field },
    fieldState: { error },
  } = useController({ name, control, rules, shouldUnregister, defaultValue });

  const getPredefinedDateValue = (key: string | null) => {
    switch (key) {
      case 'dateAccident':
        return victime?.dateAccident || null;
      case 'dateDeces':
        return victime?.dateDeces || null;
      case 'dateConsolidation':
        return procedure?.dateConsolidation || null;
      case 'dateLiquidation':
        return procedure?.dateLiquidation || null;
      default:
        return null;
    }
  };

  const getDefaultDateValue = () => {
    const defaultDatesValues = getValues('defaultDatesValues');
    const defaultDatesValue = defaultDatesValues?.[name];
    return defaultDatesValue;
  };

  useEffect(() => {
    const defaultDatesValue = getDefaultDateValue();
    const predefinedKey = defaultDatesValue;
    const predefinedDateValue = getPredefinedDateValue(predefinedKey);
    if (
      predefinedDateValue &&
      new Date(predefinedDateValue).toISOString() !== value
    ) {
      field.onChange(new Date(predefinedDateValue).toISOString());
    }
    if (defaultDatesValue) {
      const predefinedDateLabel = PREDEFINED_DATES.find(
        (date) => date.value === defaultDatesValue,
      )?.label;
      setSelectedPredefinedDate(predefinedDateLabel || null);
    } else {
      setSelectedPredefinedDate(null);
    }
  }, []);

  const availablePredefinedDates = useMemo(() => {
    const defaultDatesValue = getDefaultDateValue();
    return (
      props.availablePredefinedDates ||
      getAvailablePredefinedDates({
        procedure: procedure || {},
        victime: victime || {},
        prejudiceType,
      })
    )
      .filter((date) => getPredefinedDateValue(date) !== null)
      .map((date) => PREDEFINED_DATES.find((d) => d.value === date))
      .filter(
        (date): date is { label: string; value: EditDatesOptions } =>
          date !== undefined,
      )
      .sort((a, b) => {
        if (defaultDatesValue) {
          const selectedIndex = PREDEFINED_DATES.findIndex(
            (date) => date.value === defaultDatesValue,
          );
          if (selectedIndex >= MAX_DISPLAYED_PREDEFINED_DATES) {
            if (a.value === defaultDatesValue) return -1;
            if (b.value === defaultDatesValue) return 1;
          }
        }
        return 0;
      });
  }, [props.availablePredefinedDates, procedure, victime, prejudiceType]);

  const handleUpdateDefaultDatesValues = (
    key: string,
    value: string | null,
  ) => {
    const defaultDatesValues = getValues('defaultDatesValues');
    setValue('defaultDatesValues', {
      ...defaultDatesValues,
      [key]: value ?? null,
    });
  };

  const handleMenuItemClick = (selectedValue: string) => {
    const defaultDatesValues = getValues('defaultDatesValues');
    const predefinedValue = getPredefinedDateValue(selectedValue || '');
    if (predefinedValue) {
      field.onChange(new Date(predefinedValue).toISOString());
    } else {
      field.onChange(null);
    }

    const updatedDefaultDatesValues = {
      ...defaultDatesValues,
      [name]: selectedValue,
    };
    setValue('defaultDatesValues', updatedDefaultDatesValues);
    setSelectedPredefinedDate(
      availablePredefinedDates.find((date) => date.value === selectedValue)
        ?.label || null,
    );
    setCalendarOpen(false);
  };

  const PredefinedDates = () => (
    <InputAdornment
      position="end"
      sx={{
        display: 'flex',
        gap: 2,
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginTop: '20px',
      }}
    >
      {availablePredefinedDates
        .slice(0, MAX_DISPLAYED_PREDEFINED_DATES)
        .map((date) => (
          <Box
            key={date.value}
            onClick={() => handleMenuItemClick(date.value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              borderRadius: 1,
              padding: '2px 10px',
              border: '1px solid',
              borderColor:
                selectedPredefinedDate === date.label ? '#19A3FF' : '#d1d5db',
              color:
                selectedPredefinedDate === date.label ? '#19A3FF' : 'inherit',
              transition:
                'background-color 0.2s, color 0.2s, border-color 0.2s',
              '&:hover': {
                backgroundColor: '#f0f0f0',
                borderColor: '#19A3FF',
              },
              width: '100%',
            }}
          >
            <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
              {date.label}
            </Typography>
          </Box>
        ))}
    </InputAdornment>
  );

  const onChangeCallback = (date: Date | null) => {
    if (date === null || !date || date.toString() === 'Invalid Date') {
      field.onChange(null);
      return;
    }
    if (date < MIN_DATE || date > MAX_DATE) {
      return;
    }
    field.onChange(date.toISOString());

    handleUpdateDefaultDatesValues(name, null);
  };

  return (
    <Tooltip title={calendarOpen ? '' : error?.message || ''}>
      <Stack
        style={{
          position: 'relative',
          display: 'inline-block',
          width: '100%',
        }}
      >
        <DesktopDatePicker
          {...props}
          ref={(e) => {
            fieldRef(e);
            ref.current = e;
          }}
          label={required ? props.label + ' *' : props.label}
          value={value ? new Date(value) : null}
          {...field}
          onChange={onChangeCallback}
          onClose={() => {
            field.onBlur();
            setCalendarOpen(false);
          }}
          open={calendarOpen}
          onOpen={() => setCalendarOpen(true)}
          slots={{
            toolbar: () => {
              if (availablePredefinedDates.length === 0) {
                return null;
              }

              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px',
                    marginTop: '10px',
                  }}
                >
                  <PredefinedDates />
                </div>
              );
            },
            layout: ({ children }) => {
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    marginTop: '10px',
                  }}
                >
                  <PredefinedDates />
                  {children}
                </div>
              );
            },
          }}
          slotProps={{
            textField: {
              ...(TextFieldProps || {}),
              error: !!error,
              onBlur: field.onBlur,
            },
          }}
        />
        <InputLabel
          shrink
          sx={{
            position: 'absolute',
            justifyContent: 'flex-end',
            display: 'flex',
            marginLeft: '12px',
          }}
        >
          {selectedPredefinedDate}
        </InputLabel>
      </Stack>
    </Tooltip>
  );
};
